/**
 * Material UI Theme Configuration for Cedar Heights Music Academy Admin System
 * 
 * This theme implements the design blueprint specifications with:
 * - Custom color palette for music education context
 * - Typography optimized for readability and hierarchy
 * - Responsive breakpoints (Mobile <768px, Tablet 768-1024px, Desktop >1024px)
 * - Component overrides for consistent styling
 * - Accessibility compliance (WCAG 2.1 AA)
 * - Dynamic light/dark mode support
 */

import { createTheme, ThemeOptions, PaletteOptions, Shadows } from '@mui/material/styles';

// Light mode palette
const lightPalette: PaletteOptions = {
  mode: 'light',
  primary: {
    main: '#1976d2', // Professional blue for primary actions
    light: '#42a5f5',
    dark: '#1565c0',
    contrastText: '#ffffff',
  },
  secondary: {
    main: '#9c27b0', // Purple accent for music/creativity
    light: '#ba68c8',
    dark: '#7b1fa2',
    contrastText: '#ffffff',
  },
  success: {
    main: '#2e7d32', // Green for positive actions (attendance, payments)
    light: '#4caf50',
    dark: '#1b5e20',
    contrastText: '#ffffff',
  },
  warning: {
    main: '#ed6c02', // Orange for warnings (payment issues, deadlines)
    light: '#ff9800',
    dark: '#e65100',
    contrastText: '#ffffff',
  },
  error: {
    main: '#d32f2f', // Red for errors and critical alerts
    light: '#f44336',
    dark: '#c62828',
    contrastText: '#ffffff',
  },
  info: {
    main: '#0288d1', // Light blue for informational content
    light: '#03a9f4',
    dark: '#01579b',
    contrastText: '#ffffff',
  },
  background: {
    default: '#fafafa', // Light gray background
    paper: '#ffffff', // White for cards and surfaces
  },
  text: {
    primary: 'rgba(0, 0, 0, 0.87)', // High contrast for primary text
    secondary: 'rgba(0, 0, 0, 0.6)', // Medium contrast for secondary text
    disabled: 'rgba(0, 0, 0, 0.38)', // Low contrast for disabled text
  },
  divider: 'rgba(0, 0, 0, 0.12)', // Subtle dividers
  grey: {
    50: '#fafafa',
    100: '#f5f5f5',
    200: '#eeeeee',
    300: '#e0e0e0',
    400: '#bdbdbd',
    500: '#9e9e9e',
    600: '#757575',
    700: '#616161',
    800: '#424242',
    900: '#212121',
  },
};

// Dark mode palette
const darkPalette: PaletteOptions = {
  mode: 'dark',
  primary: {
    main: '#90caf9', // Lighter blue for dark mode
    light: '#bbdefb',
    dark: '#42a5f5',
    contrastText: '#000000',
  },
  secondary: {
    main: '#ce93d8', // Lighter purple for dark mode
    light: '#f3e5f5',
    dark: '#ab47bc',
    contrastText: '#000000',
  },
  success: {
    main: '#66bb6a', // Lighter green for dark mode
    light: '#81c784',
    dark: '#388e3c',
    contrastText: '#000000',
  },
  warning: {
    main: '#ffa726', // Lighter orange for dark mode
    light: '#ffb74d',
    dark: '#f57c00',
    contrastText: '#000000',
  },
  error: {
    main: '#f44336', // Lighter red for dark mode
    light: '#e57373',
    dark: '#d32f2f',
    contrastText: '#ffffff',
  },
  info: {
    main: '#29b6f6', // Lighter blue for dark mode
    light: '#4fc3f7',
    dark: '#0288d1',
    contrastText: '#000000',
  },
  background: {
    default: '#121212', // Dark background
    paper: '#1e1e1e', // Dark paper background
  },
  text: {
    primary: 'rgba(255, 255, 255, 0.87)', // High contrast white text
    secondary: 'rgba(255, 255, 255, 0.6)', // Medium contrast white text
    disabled: 'rgba(255, 255, 255, 0.38)', // Low contrast white text
  },
  divider: 'rgba(255, 255, 255, 0.12)', // Subtle white dividers
  grey: {
    50: '#fafafa',
    100: '#f5f5f5',
    200: '#eeeeee',
    300: '#e0e0e0',
    400: '#bdbdbd',
    500: '#9e9e9e',
    600: '#757575',
    700: '#616161',
    800: '#424242',
    900: '#212121',
  },
};

// Typography configuration optimized for readability and hierarchy
const typography = {
  fontFamily: [
    '-apple-system',
    'BlinkMacSystemFont',
    '"Segoe UI"',
    'Roboto',
    '"Helvetica Neue"',
    'Arial',
    'sans-serif',
    '"Apple Color Emoji"',
    '"Segoe UI Emoji"',
    '"Segoe UI Symbol"',
  ].join(','),
  
  // Heading styles
  h1: {
    fontSize: '2.125rem', // 34px
    fontWeight: 600,
    lineHeight: 1.2,
    letterSpacing: '-0.01562em',
  },
  h2: {
    fontSize: '1.75rem', // 28px
    fontWeight: 600,
    lineHeight: 1.3,
    letterSpacing: '-0.00833em',
  },
  h3: {
    fontSize: '1.5rem', // 24px
    fontWeight: 600,
    lineHeight: 1.4,
    letterSpacing: '0em',
  },
  h4: {
    fontSize: '1.25rem', // 20px
    fontWeight: 600,
    lineHeight: 1.4,
    letterSpacing: '0.00735em',
  },
  h5: {
    fontSize: '1.125rem', // 18px
    fontWeight: 600,
    lineHeight: 1.5,
    letterSpacing: '0em',
  },
  h6: {
    fontSize: '1rem', // 16px
    fontWeight: 600,
    lineHeight: 1.5,
    letterSpacing: '0.0075em',
  },
  
  // Body text styles
  body1: {
    fontSize: '1rem', // 16px
    fontWeight: 400,
    lineHeight: 1.5,
    letterSpacing: '0.00938em',
  },
  body2: {
    fontSize: '0.875rem', // 14px
    fontWeight: 400,
    lineHeight: 1.43,
    letterSpacing: '0.01071em',
  },
  
  // UI element styles
  button: {
    fontSize: '0.875rem', // 14px
    fontWeight: 500,
    lineHeight: 1.75,
    letterSpacing: '0.02857em',
    textTransform: 'none' as const, // Disable uppercase transformation
  },
  caption: {
    fontSize: '0.75rem', // 12px
    fontWeight: 400,
    lineHeight: 1.66,
    letterSpacing: '0.03333em',
  },
  overline: {
    fontSize: '0.75rem', // 12px
    fontWeight: 400,
    lineHeight: 2.66,
    letterSpacing: '0.08333em',
    textTransform: 'uppercase' as const,
  },
};

// Responsive breakpoints (mobile-first approach)
const breakpoints = {
  values: {
    xs: 0,
    sm: 768,  // Tablet breakpoint
    md: 1024, // Desktop breakpoint
    lg: 1440, // Large desktop breakpoint
    xl: 1920, // Extra large desktop
  },
};

// Spacing configuration (8px base unit)
const spacing = 8;

// Function to create components based on mode
const createComponents = (mode: 'light' | 'dark') => ({
  // Button overrides
  MuiButton: {
    styleOverrides: {
      root: {
        borderRadius: 8, // Rounded corners
        textTransform: 'none' as const,
        fontWeight: 500,
        minHeight: 44, // Minimum touch target size
        padding: '8px 16px',
      },
      containedPrimary: {
        boxShadow: mode === 'dark'
          ? '0 2px 4px rgba(25, 118, 210, 0.3)'
          : '0 2px 4px rgba(25, 118, 210, 0.2)',
        '&:hover': {
          boxShadow: mode === 'dark'
            ? '0 4px 8px rgba(0, 150, 255, 0.4), 0 0 12px rgba(0, 150, 255, 0.2)'
            : '0 4px 8px rgba(25, 118, 210, 0.3)',
        },
      },
    },
  },
  
  // Card overrides with universal dark mode shadows
  MuiCard: {
    styleOverrides: {
      root: {
        borderRadius: 12, // More rounded corners for cards
        boxShadow: mode === 'dark'
          ? '0 2px 8px rgba(0, 0, 0, 0.4)'
          : '0 2px 8px rgba(0, 0, 0, 0.1)',
        '&:hover': {
          boxShadow: mode === 'dark'
            ? '0 4px 16px rgba(0, 150, 255, 0.5), 0 0 20px rgba(0, 150, 255, 0.3)'
            : '0 4px 16px rgba(0, 0, 0, 0.15)',
        },
      },
    },
  },
  
  // Paper overrides with dark mode support
  MuiPaper: {
    styleOverrides: {
      root: {
        borderRadius: 8,
      },
      elevation1: {
        boxShadow: mode === 'dark'
          ? '0 2px 4px rgba(0, 0, 0, 0.3)'
          : '0 2px 4px rgba(0, 0, 0, 0.1)',
      },
      elevation2: {
        boxShadow: mode === 'dark'
          ? '0 4px 8px rgba(0, 0, 0, 0.32)'
          : '0 4px 8px rgba(0, 0, 0, 0.12)',
      },
      elevation3: {
        boxShadow: mode === 'dark'
          ? '0 6px 12px rgba(0, 0, 0, 0.35)'
          : '0 6px 12px rgba(0, 0, 0, 0.15)',
      },
      elevation4: {
        boxShadow: mode === 'dark'
          ? '0 8px 16px rgba(255, 255, 255, 0.15), 0 0 40px rgba(255, 255, 255, 0.1)'
          : '0 8px 16px rgba(0, 0, 0, 0.15)',
      },
    },
  },
  
  // AppBar overrides
  MuiAppBar: {
    styleOverrides: {
      root: {
        boxShadow: mode === 'dark'
          ? '0 2px 4px rgba(0, 0, 0, 0.3)'
          : '0 2px 4px rgba(0, 0, 0, 0.1)',
        backdropFilter: 'blur(8px)',
      },
    },
  },
  
  // Drawer overrides
  MuiDrawer: {
    styleOverrides: {
      paper: {
        borderRight: mode === 'dark'
          ? '1px solid rgba(255, 255, 255, 0.12)'
          : '1px solid rgba(0, 0, 0, 0.12)',
        boxShadow: 'none',
      },
    },
  },
  
  // TextField overrides
  MuiTextField: {
    styleOverrides: {
      root: {
        '& .MuiOutlinedInput-root': {
          borderRadius: 8,
        },
      },
    },
  },
  
  // Chip overrides
  MuiChip: {
    styleOverrides: {
      root: {
        borderRadius: 16,
        fontWeight: 500,
      },
    },
  },
  
  // Alert overrides
  MuiAlert: {
    styleOverrides: {
      root: {
        borderRadius: 8,
        fontWeight: 500,
      },
    },
  },
  
  // Table overrides
  MuiTableCell: {
    styleOverrides: {
      root: {
        borderBottom: mode === 'dark'
          ? '1px solid rgba(255, 255, 255, 0.06)'
          : '1px solid rgba(0, 0, 0, 0.06)',
      },
      head: {
        fontWeight: 600,
        backgroundColor: mode === 'dark'
          ? 'rgba(255, 255, 255, 0.02)'
          : 'rgba(0, 0, 0, 0.02)',
      },
    },
  },
});

// Function to create theme based on mode
export const createAppTheme = (mode: 'light' | 'dark') => {
  const palette = mode === 'light' ? lightPalette : darkPalette;
  
  // Adjust shadows for dark mode
  const shadows = mode === 'dark' ? [
    'none',
    '0 2px 4px rgba(0, 0, 0, 0.3)',
    '0 4px 8px rgba(0, 0, 0, 0.32)',
    '0 6px 12px rgba(0, 0, 0, 0.35)',
    '0 8px 16px rgba(0, 0, 0, 0.35)',
    '0 10px 20px rgba(0, 0, 0, 0.35)',
    '0 12px 24px rgba(0, 0, 0, 0.35)',
    '0 14px 28px rgba(0, 0, 0, 0.35)',
    '0 16px 32px rgba(0, 0, 0, 0.35)',
    '0 18px 36px rgba(0, 0, 0, 0.35)',
    '0 20px 40px rgba(0, 0, 0, 0.35)',
    '0 22px 44px rgba(0, 0, 0, 0.35)',
    '0 24px 48px rgba(0, 0, 0, 0.35)',
    '0 26px 52px rgba(0, 0, 0, 0.35)',
    '0 28px 56px rgba(0, 0, 0, 0.35)',
    '0 30px 60px rgba(0, 0, 0, 0.35)',
    '0 32px 64px rgba(0, 0, 0, 0.35)',
    '0 34px 68px rgba(0, 0, 0, 0.35)',
    '0 36px 72px rgba(0, 0, 0, 0.35)',
    '0 38px 76px rgba(0, 0, 0, 0.35)',
    '0 40px 80px rgba(0, 0, 0, 0.35)',
    '0 42px 84px rgba(0, 0, 0, 0.35)',
    '0 44px 88px rgba(0, 0, 0, 0.35)',
    '0 46px 92px rgba(0, 0, 0, 0.35)',
    '0 48px 96px rgba(0, 0, 0, 0.35)',
  ] : [
    'none',
    '0 2px 4px rgba(0, 0, 0, 0.1)',
    '0 4px 8px rgba(0, 0, 0, 0.12)',
    '0 6px 12px rgba(0, 0, 0, 0.15)',
    '0 8px 16px rgba(0, 0, 0, 0.15)',
    '0 10px 20px rgba(0, 0, 0, 0.15)',
    '0 12px 24px rgba(0, 0, 0, 0.15)',
    '0 14px 28px rgba(0, 0, 0, 0.15)',
    '0 16px 32px rgba(0, 0, 0, 0.15)',
    '0 18px 36px rgba(0, 0, 0, 0.15)',
    '0 20px 40px rgba(0, 0, 0, 0.15)',
    '0 22px 44px rgba(0, 0, 0, 0.15)',
    '0 24px 48px rgba(0, 0, 0, 0.15)',
    '0 26px 52px rgba(0, 0, 0, 0.15)',
    '0 28px 56px rgba(0, 0, 0, 0.15)',
    '0 30px 60px rgba(0, 0, 0, 0.15)',
    '0 32px 64px rgba(0, 0, 0, 0.15)',
    '0 34px 68px rgba(0, 0, 0, 0.15)',
    '0 36px 72px rgba(0, 0, 0, 0.15)',
    '0 38px 76px rgba(0, 0, 0, 0.15)',
    '0 40px 80px rgba(0, 0, 0, 0.15)',
    '0 42px 84px rgba(0, 0, 0, 0.15)',
    '0 44px 88px rgba(0, 0, 0, 0.15)',
    '0 46px 92px rgba(0, 0, 0, 0.15)',
    '0 48px 96px rgba(0, 0, 0, 0.15)',
  ];

  const themeOptions: ThemeOptions = {
    palette,
    typography,
    breakpoints,
    spacing,
    components: createComponents(mode),
    shape: {
      borderRadius: 8, // Default border radius
    },
    shadows: shadows as Shadows,
  };

  return createTheme(themeOptions);
};

// Create and export the default light theme for backward compatibility
export const theme = createAppTheme('light');

// Export theme utilities for use in components
export const getResponsiveValue = (mobile: unknown, tablet?: unknown, desktop?: unknown) => ({
  xs: mobile,
  sm: tablet || mobile,
  md: desktop || tablet || mobile,
});

export const getSpacing = (multiplier: number) => theme.spacing(multiplier);

// Universal shadow system for all components
export type ShadowIntensity = 'light' | 'medium' | 'heavy';
export type ShadowVariant = 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info';

/**
 * Universal shadow utility function for all components
 * Provides consistent shadows across light and dark modes
 */
export const getUniversalShadow = (
  intensity: ShadowIntensity = 'light',
  mode: 'light' | 'dark' = 'light',
  variant: ShadowVariant = 'default'
) => {
  const baseShadows = mode === 'dark' ? {
    light: '0 2px 8px rgba(0, 0, 0, 0.4)',
    medium: '0 4px 16px rgba(0, 0, 0, 0.45)',
    heavy: '0 8px 24px rgba(0, 0, 0, 0.5)',
  } : {
    light: '0 2px 8px rgba(0, 0, 0, 0.1)',
    medium: '0 4px 16px rgba(0, 0, 0, 0.15)',
    heavy: '0 8px 24px rgba(0, 0, 0, 0.2)',
  };

  // Variant-specific shadows for dark mode with subtle color tints
  const variantShadows = mode === 'dark' ? {
    default: baseShadows,
    primary: {
      light: '0 2px 8px rgba(25, 118, 210, 0.2)',
      medium: '0 4px 16px rgba(25, 118, 210, 0.25)',
      heavy: '0 8px 24px rgba(25, 118, 210, 0.3)',
    },
    secondary: {
      light: '0 2px 8px rgba(156, 39, 176, 0.2)',
      medium: '0 4px 16px rgba(156, 39, 176, 0.25)',
      heavy: '0 8px 24px rgba(156, 39, 176, 0.3)',
    },
    success: {
      light: '0 2px 8px rgba(46, 125, 50, 0.2)',
      medium: '0 4px 16px rgba(46, 125, 50, 0.25)',
      heavy: '0 8px 24px rgba(46, 125, 50, 0.3)',
    },
    warning: {
      light: '0 2px 8px rgba(237, 108, 2, 0.2)',
      medium: '0 4px 16px rgba(237, 108, 2, 0.25)',
      heavy: '0 8px 24px rgba(237, 108, 2, 0.3)',
    },
    error: {
      light: '0 2px 8px rgba(211, 47, 47, 0.2)',
      medium: '0 4px 16px rgba(211, 47, 47, 0.25)',
      heavy: '0 8px 24px rgba(211, 47, 47, 0.3)',
    },
    info: {
      light: '0 2px 8px rgba(2, 136, 209, 0.2)',
      medium: '0 4px 16px rgba(2, 136, 209, 0.25)',
      heavy: '0 8px 24px rgba(2, 136, 209, 0.3)',
    },
  } : {
    default: baseShadows,
    primary: baseShadows,
    secondary: baseShadows,
    success: baseShadows,
    warning: baseShadows,
    error: baseShadows,
    info: baseShadows,
  };

  return variantShadows[variant][intensity];
};

/**
 * Universal hover shadow utility function for all components
 * Provides vibrant hover effects that work well in both light and dark modes
 */
export const getUniversalHoverShadow = (
  intensity: ShadowIntensity = 'light',
  mode: 'light' | 'dark' = 'light',
  variant: ShadowVariant = 'default'
) => {
  const baseHoverShadows = mode === 'dark' ? {
    light: '0 2px 12px rgba(255, 255, 255, 0.1), 0 0 16px rgba(255, 255, 255, 0.05)',
    medium: '0 4px 20px rgba(255, 255, 255, 0.12), 0 0 24px rgba(255, 255, 255, 0.08)',
    heavy: '0 8px 32px rgba(255, 255, 255, 0.15), 0 0 40px rgba(255, 255, 255, 0.1)',
  } : {
    light: '0 2px 12px rgba(0, 0, 0, 0.15)',
    medium: '0 4px 20px rgba(0, 0, 0, 0.2)',
    heavy: '0 8px 32px rgba(0, 0, 0, 0.25)',
  };

  // Vibrant hover shadows with color-specific glows for dark mode
  const variantHoverShadows = mode === 'dark' ? {
    default: baseHoverShadows,
    primary: {
      light: '0 2px 8px rgba(0, 150, 255, 0.4), 0 0 12px rgba(0, 150, 255, 0.2)',
      medium: '0 4px 16px rgba(0, 150, 255, 0.5), 0 0 20px rgba(0, 150, 255, 0.3)',
      heavy: '0 8px 24px rgba(0, 150, 255, 0.6), 0 0 32px rgba(0, 150, 255, 0.4)',
    },
    secondary: {
      light: '0 2px 8px rgba(206, 147, 216, 0.4), 0 0 12px rgba(206, 147, 216, 0.2)',
      medium: '0 4px 16px rgba(206, 147, 216, 0.5), 0 0 20px rgba(206, 147, 216, 0.3)',
      heavy: '0 8px 24px rgba(206, 147, 216, 0.6), 0 0 32px rgba(206, 147, 216, 0.4)',
    },
    success: {
      light: '0 2px 8px rgba(102, 187, 106, 0.4), 0 0 12px rgba(102, 187, 106, 0.2)',
      medium: '0 4px 16px rgba(102, 187, 106, 0.5), 0 0 20px rgba(102, 187, 106, 0.3)',
      heavy: '0 8px 24px rgba(102, 187, 106, 0.6), 0 0 32px rgba(102, 187, 106, 0.4)',
    },
    warning: {
      light: '0 2px 8px rgba(255, 167, 38, 0.4), 0 0 12px rgba(255, 167, 38, 0.2)',
      medium: '0 4px 16px rgba(255, 167, 38, 0.5), 0 0 20px rgba(255, 167, 38, 0.3)',
      heavy: '0 8px 24px rgba(255, 167, 38, 0.6), 0 0 32px rgba(255, 167, 38, 0.4)',
    },
    error: {
      light: '0 2px 8px rgba(244, 67, 54, 0.4), 0 0 12px rgba(244, 67, 54, 0.2)',
      medium: '0 4px 16px rgba(244, 67, 54, 0.5), 0 0 20px rgba(244, 67, 54, 0.3)',
      heavy: '0 8px 24px rgba(244, 67, 54, 0.6), 0 0 32px rgba(244, 67, 54, 0.4)',
    },
    info: {
      light: '0 2px 8px rgba(41, 182, 246, 0.4), 0 0 12px rgba(41, 182, 246, 0.2)',
      medium: '0 4px 16px rgba(41, 182, 246, 0.5), 0 0 20px rgba(41, 182, 246, 0.3)',
      heavy: '0 8px 24px rgba(41, 182, 246, 0.6), 0 0 32px rgba(41, 182, 246, 0.4)',
    },
  } : {
    default: baseHoverShadows,
    primary: baseHoverShadows,
    secondary: baseHoverShadows,
    success: baseHoverShadows,
    warning: baseHoverShadows,
    error: baseHoverShadows,
    info: baseHoverShadows,
  };

  return variantHoverShadows[variant][intensity];
};

// Legacy payment-specific shadows (maintained for backward compatibility)
export const getPaymentShadow = (intensity: ShadowIntensity = 'light', mode: 'light' | 'dark' = 'light') => {
  return getUniversalShadow(intensity, mode, 'primary');
};

export const getPaymentHoverShadow = (intensity: ShadowIntensity = 'light', mode: 'light' | 'dark' = 'light') => {
  return getUniversalHoverShadow(intensity, mode, 'primary');
};

/**
 * Convenience function to get shadow styles for sx prop
 * Returns both normal and hover shadows with transition
 */
export const getShadowStyles = (
  intensity: ShadowIntensity = 'light',
  mode: 'light' | 'dark' = 'light',
  variant: ShadowVariant = 'default'
) => ({
  boxShadow: getUniversalShadow(intensity, mode, variant),
  transition: 'box-shadow 0.3s ease-in-out',
  '&:hover': {
    boxShadow: getUniversalHoverShadow(intensity, mode, variant),
  },
});

export default theme;